// Colour arithmetic for the light ground's inks (2026-09-11; ported from the
// parked light-mode branch of 2026-09-09).
//
// Every item type, brand and industry carries ONE identity colour
// (config/vocab.js) — authored for the dark ground the app shipped on, where
// a pastel reads as a light on black. On the gradient ground the same pastel
// is a smudge (user call 2026-09-11: "some tags are not visible", "colors
// used are sometimes too light for this design"), and forty-odd brands is
// too many to author twice by hand, so the vocabulary DERIVES its inks from
// the authored ones: same hue, same saturation, lightness lowered step by
// step until the ink clears a WCAG contrast bar on the surface it has to
// read on. Hue is what says WHO (Compare README), and hue is exactly what
// this keeps.
//
// Pure functions, no theme knowledge: the caller decides the ground and the
// bar. Results are memoised per input, since bstyle()/tmeta() run per row.

const clamp01 = (v) => Math.min(1, Math.max(0, v));

export function hexToRgb(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export const rgbToHex = ([r, g, b]) =>
  "#" +
  [r, g, b]
    .map((v) =>
      Math.round(clamp01(v / 255) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("");

export function rgbToHsl([r, g, b]) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
}

export function hslToRgb([h, s, l]) {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = h / 360;
  const chan = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [chan(hk + 1 / 3) * 255, chan(hk) * 255, chan(hk - 1 / 3) * 255];
}

// WCAG 2.x relative luminance and contrast ratio
export function luminance([r, g, b]) {
  const lin = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrast(hexA, hexB) {
  const la = luminance(hexToRgb(hexA));
  const lb = luminance(hexToRgb(hexB));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// The surface an ink must read on: WHITE — the composer, and the frosted
// cards, which read as near-white over the pale stops of the gradient. An
// ink that clears the bar there clears it on every card in the app.
export const INK_GROUND = "#ffffff";

const cache = new Map();

// Lower the lightness of `hex` (hue and saturation kept) until it reads at
// `min`:1 on `ground`. A colour that already passes comes back unchanged;
// anything that is not a hex string passes through untouched.
export function deepen(hex, ground = INK_GROUND, min = 4.5) {
  if (typeof hex !== "string" || !hex.startsWith("#")) return hex;
  const key = `${hex}|${ground}|${min}`;
  if (cache.has(key)) return cache.get(key);
  let out = hex;
  if (contrast(hex, ground) < min) {
    const [h, s, l0] = rgbToHsl(hexToRgb(hex));
    let l = l0;
    while (l > 0.08) {
      l -= 0.02;
      const candidate = rgbToHex(hslToRgb([h, s, l]));
      out = candidate; // darkest tried so far — the floor if nothing passes
      if (contrast(candidate, ground) >= min) break;
    }
  }
  cache.set(key, out);
  return out;
}

// The mirror step for a DARK surface (2026-09-28, the compare screen on the
// dusk): raise the lightness of `hex` (hue and saturation kept) until it
// reads at `min`:1 on `ground`. The authored pastels were drawn for a black
// ground, but the derived paper inks are what the tables render, and on the
// violet gradient those read as a smudge — so a screen on a gradient asks
// for this variant instead. A colour that already passes comes back
// unchanged; anything that is not a hex string passes through untouched.
export function brighten(hex, ground, min = 4.5) {
  if (typeof hex !== "string" || !hex.startsWith("#")) return hex;
  const key = `^${hex}|${ground}|${min}`;
  if (cache.has(key)) return cache.get(key);
  let out = hex;
  if (contrast(hex, ground) < min) {
    const [h, s, l0] = rgbToHsl(hexToRgb(hex));
    let l = l0;
    while (l < 0.92) {
      l += 0.02;
      const candidate = rgbToHex(hslToRgb([h, s, l]));
      out = candidate; // lightest tried so far — the ceiling if nothing passes
      if (contrast(candidate, ground) >= min) break;
    }
  }
  cache.set(key, out);
  return out;
}
